import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { HttpService } from 'src/app/services/http.service';
import { toSlug } from 'src/app/utils/slug.formatter';
import { ButtonModule } from '../button/button.module';
import { TextfieldComponent } from '../textfield/textfield.component';

export interface NamedFile {
  url?: string;
  file?: File | null;
}

// A list of named files: each row has a name, and a file that's picked (or, with `allowUrl`, a URL that's pasted).
@Component({
  selector: 'app-named-file-list',
  templateUrl: './named-file-list.component.html',
  styleUrls: ['./named-file-list.component.scss'],
  imports: [ButtonModule, TextfieldComponent],
})
export class NamedFileListComponent implements OnInit, OnChanges {
  private readonly httpService = inject(HttpService);

  @Input() id = 'nfl';
  @Input() entityName!: string;
  @Input() label?: string;
  @Input() values?: NamedFile[];
  @Input() nameKey = 'title';
  @Input() accept = 'image/*,application/pdf';
  @Input() uploadPlaceholder = 'Upload PDF or PNG';
  @Input() uploadOnPick = false;
  @Input() nameFromFileName = false;
  @Input() allowUrl = false;
  @Input() allowCopy = true;
  @Input() disabled = false;
  @Input() errors: string[] = [];
  @Input() fieldErrors: { [path: string]: string } = {};
  @Input() errorPath = '';

  @Output() valuesChange = new EventEmitter<NamedFile[]>();

  items: NamedFile[] = [];
  uploadingCount = 0;
  uploadError = '';
  copiedIndex: number | null = null; // The row whose URL was just copied, so its button can say so.

  ngOnInit(): void {
    this.items = this.values ?? [this.emptyRow()];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['values'] && !changes['values'].firstChange && this.values) {
      this.items = this.values;
    }
  }

  get listLabel(): string {
    return this.label ?? (this.entityName ? `${this.entityName}s` : '');
  }

  nameOf(item: NamedFile): string {
    return (item as Record<string, any>)[this.nameKey] ?? '';
  }

  nameError(index: number): string {
    return this.errors?.includes(this.entityName + index) ? 'This field is required.' : this.rowError(index, this.nameKey);
  }

  rowError(index: number, key: string): string {
    return this.fieldErrors?.[`${this.errorPath}[${index}].${key}`] ?? '';
  }

  get listError(): string {
    return this.fieldErrors?.[this.errorPath] ?? '';
  }

  addNew(): void {
    this.emit([...this.items, this.emptyRow()]);
  }

  onNameChange(name: string, index: number): void {
    this.update(index, { [this.nameKey]: name });
  }

  onUrlChange(url: string, index: number): void {
    this.update(index, { url });
  }

  removeItem(index: number): void {
    this.emit(this.items.filter((_item, i) => i !== index));
  }

  onFileChange(input: HTMLInputElement, index: number): void {
    const file = input.files?.item(0);
    input.value = ''; // So picking the same file again still counts as a change.
    if (!file) {
      return;
    }
    if (!this.uploadOnPick) {
      this.update(index, { url: '', file, ...this.nameFor(file, index) });
      return;
    }
    this.uploadingCount++;
    this.uploadError = '';
    this.httpService.uploadMedia(file, uploadKind(file)).subscribe(
      response => {
        this.uploadingCount--;
        this.update(index, { url: response.secure_url, ...this.nameFor(file, index) });
      },
      () => {
        this.uploadingCount--;
        this.uploadError = `Couldn't upload ${file.name}. Please try again.`;
      }
    );
  }

  copyUrl(index: number): void {
    (navigator.clipboard?.writeText(this.items[index].url ?? '') ?? Promise.reject()).then(
      () => {
        this.copiedIndex = index;
        setTimeout(() => (this.copiedIndex = null), 2000);
      },
      () => (this.uploadError = "Couldn't copy the URL. Please try again.")
    );
  }

  // With `nameFromFileName`, a row with no name yet is named after its file. Example: "Jazz Mascot.PNG" becomes "jazz-mascot", or "jazz-mascot-2" if that's taken.
  private nameFor(file: File, index: number): Record<string, string> {
    if (!this.nameFromFileName || this.nameOf(this.items[index]).trim()) {
      return {};
    }
    const names = new Set(this.items.map(item => this.nameOf(item)));
    const base = toSlug(file.name.replace(/\.[^.]*$/, '')) || 'file';
    let name = base;
    for (let n = 2; names.has(name); n++) {
      name = `${base}-${n}`;
    }
    return { [this.nameKey]: name };
  }

  private update(index: number, changes: Record<string, unknown>): void {
    this.emit(this.items.map((item, i) => (i === index ? { ...item, ...changes } : item)));
  }

  private emit(items: NamedFile[]): void {
    this.items = items;
    this.valuesChange.emit(items);
  }

  private emptyRow(): NamedFile {
    return { [this.nameKey]: '', url: '' };
  }
}

// Cloudinary keeps images, and videos and sounds (both under "video"), apart from everything else.
const uploadKind = (file: File): 'image' | 'video' | 'file' => {
  if (file.type.startsWith('image/')) {
    return 'image';
  }
  return file.type.startsWith('audio/') || file.type.startsWith('video/') ? 'video' : 'file';
};
